import { ErrorBoundaryContent } from 'components';
import Alert from 'components/Alert/Alert';
import NavigationLayoutProvider from 'components/AppShell/NavigationLayoutProvider';
import RecentHistoryProvider from 'components/AppShell/RecentHistoryProvider';
import Button from 'components/Button/Button';
import SuspenseLoadingIndicator from 'components/LoadingPageIndicator/SuspenseLoadingIndicator';
import { useCustomTranslation } from 'helpers/translationHelper';
import React, { useEffect, useMemo } from 'react';
import TagManager from 'react-gtm-module';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import AnnouncementsProvider from './AnnouncementsProvider';
import {
  useGetCompanyAndUserInfoQuery,
  useGetFeatureFlagsQuery,
  useGetUserConfigurationQueryV2
} from './queries';
import RouteErrorBoundary from './RouteErrorBoundary';
import useAuth from './useAuth';

interface Props {}

// const PRODUCTFRUITS_WORKSPACE = "bhqd2Xu2DHcUxLeu";
const GTM_ID = 'GTM-TTGQZJC7';
export const TECHNICIAN_RESTRICTED_ROUTES = ['/serviceObjects', '/customers', '/customForms'];

// https://dev.to/iamandrewluca/private-route-in-react-router-v6-lg5

const PrivateOutlet: React.FC<Props> = (): React.ReactElement => {
  const { authenticated } = useAuth();
  const location = useLocation();

  const companyAndUserInfoQuery = useGetCompanyAndUserInfoQuery(authenticated);
  const configurationQuery = useGetUserConfigurationQueryV2(authenticated);
  const featureFlagsQuery = useGetFeatureFlagsQuery(authenticated);
  const { tCommon } = useCustomTranslation();

  const restrictTechnicianDataVisibility =
    companyAndUserInfoQuery.data?.company.restrictTechnicianDataVisibility;

  const user = companyAndUserInfoQuery.data?.user;
  const isManager = user?.isManager;
  const isReadonly = user?.isReadonly;
  const isAdmin = user?.isAdmin;

  const tagManagerArgs = useMemo(
    () => ({
      gtmId: GTM_ID
    }),
    []
  );

  useEffect(() => {
    TagManager.initialize(tagManagerArgs);
  }, [tagManagerArgs]);

  if (!authenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  // Missing initial data blocks the first layout, but background refetches keep the app mounted.
  const startupQueries = [companyAndUserInfoQuery, configurationQuery, featureFlagsQuery];
  const missingQueries = startupQueries.filter((query) => query.data === undefined);
  if (missingQueries.length > 0) {
    if (missingQueries.some((query) => query.isError && !query.isFetching)) {
      return (
        <Alert
          type="error"
          message={tCommon('environment_load_failed', {
            defaultValue: 'Nastavení aplikace se nepodařilo načíst.'
          })}
          action={
            <Button
              onClick={() => {
                missingQueries.forEach((query) => {
                  void query.refetch();
                });
              }}
            >
              {tCommon('retry')}
            </Button>
          }
        />
      );
    }
    return <SuspenseLoadingIndicator />;
  }

  if (
    !isAdmin &&
    !isManager &&
    !isReadonly &&
    restrictTechnicianDataVisibility &&
    TECHNICIAN_RESTRICTED_ROUTES.some((route) => location.pathname.startsWith(route))
  )
    return <Navigate to="/" />;

  return (
    <RecentHistoryProvider>
      <NavigationLayoutProvider
        key={`${user?.companyId}:${user?.id}`}
        isSuperAdmin={user?.isSuperAdmin}
        initialConfiguration={configurationQuery.data}
      >
        <AnnouncementsProvider>
          {/* {user && (
        <ProductFruits
          language={language || languageId || 'cs'}
          user={{
            username: user.username,
            email: user.email,
            firstname: user.firstName,
            lastname: user.lastName,
            role: getRolesText(user),
            props: {
              userId: user.id,
              enviroment: getEnvironmentName() || 'localhost',
              tenantCode: user.tenantCode
            }
          }}
          workspaceCode={PRODUCTFRUITS_WORKSPACE}
        />
      )} */}
          <div className="App_inner">
            <RouteErrorBoundary
              fallback={(fallbackProps) => <ErrorBoundaryContent fallbackProps={fallbackProps} />}
            >
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </AnnouncementsProvider>
      </NavigationLayoutProvider>
    </RecentHistoryProvider>
  );
};

export default PrivateOutlet;
